/**
 * v-resizable-columns directive
 *
 * Usage: <b-table v-resizable-columns="{ tableId: 'my-table', minWidth: 80, maxWidth: 600 }" ...>
 *
 * Options:
 * - tableId: Unique ID for storing column widths in localStorage
 * - minWidth: Minimum column width (default: 80px)
 * - maxWidth: Maximum column width (default: 600px)
 * - exclude: Array of column keys to exclude from resizing
 */

// State management for active resize operations
const resizeState = {
  isResizing: false,
  currentTable: null,
  currentColumn: null,
  startX: 0,
  startWidth: 0,
  columnWidths: {},
};

// Helper to get stored column widths
function loadColumnWidths(tableId) {
  if (!tableId) return {};

  const key = `table_column_widths_${tableId}`;
  const saved = localStorage.getItem(key);

  if (saved) {
    try {
      return JSON.parse(saved);
    } catch (e) {
      console.error('Error loading column widths:', e);
      return {};
    }
  }

  return {};
}

// Helper to save column widths
function saveColumnWidths(tableId, widths) {
  if (!tableId) return;

  const key = `table_column_widths_${tableId}`;
  localStorage.setItem(key, JSON.stringify(widths));
}

// Helper to find column key by index
function getColumnKey(el, index) {
  const table = el.querySelector('table');
  if (!table) return null;

  // Try to get from Vue component instance
  const vueInstance = el.__vue__;
  if (vueInstance) {
    // For b-table, fields are in the component data
    const fields = vueInstance.fields || vueInstance.computedFields;
    if (fields && fields[index]) {
      return fields[index].key;
    }
  }

  // Try to get from th aria-colindex attribute (b-table sets this)
  const header = table.querySelector(`thead th:nth-child(${index + 1})`);
  const ariaColIndex = header?.getAttribute('aria-colindex');
  if (ariaColIndex && vueInstance?.fields) {
    const field = vueInstance.fields[parseInt(ariaColIndex) - 1];
    if (field) return field.key;
  }

  // Fallback to data attributes or index
  return header?.getAttribute('data-field-key') || `col-${index}`;
}

// Create resize handle element
function createResizeHandle() {
  const handle = document.createElement('div');
  handle.className = 'column-resize-handle';
  handle.style.cssText = `
    position: absolute;
    right: 0;
    top: 0;
    bottom: 0;
    width: 8px;
    cursor: col-resize;
    user-select: none;
    z-index: 10;
    transition: background-color 0.2s;
  `;

  // Hover effect
  handle.addEventListener('mouseenter', () => {
    if (!resizeState.isResizing) {
      handle.style.backgroundColor = 'rgba(59, 130, 246, 0.3)';
    }
  });

  handle.addEventListener('mouseleave', () => {
    if (!resizeState.isResizing) {
      handle.style.backgroundColor = 'transparent';
    }
  });

  return handle;
}

// Apply column width
function applyColumnWidth(table, columnIndex, width) {
  // Apply to headers
  const headers = table.querySelectorAll(
    `thead th:nth-child(${columnIndex + 1})`,
  );
  headers.forEach((header) => {
    header.style.width = `${width}px`;
    header.style.minWidth = `${width}px`;
    header.style.maxWidth = `${width}px`;
  });

  // Apply to cells
  const cells = table.querySelectorAll(
    `tbody td:nth-child(${columnIndex + 1})`,
  );
  cells.forEach((cell) => {
    cell.style.width = `${width}px`;
    cell.style.minWidth = `${width}px`;
    cell.style.maxWidth = `${width}px`;
  });
}

// Handle resize start
function handleResizeStart(e, el, header, columnIndex, options) {
  e.preventDefault();
  e.stopPropagation();

  const touch = e.type.includes('touch') ? e.touches[0] : e;

  resizeState.isResizing = true;
  resizeState.currentTable = el;
  resizeState.currentColumn = getColumnKey(el, columnIndex);
  resizeState.startX = touch.clientX;
  resizeState.startWidth = header.offsetWidth;

  // Add resizing class
  document.body.classList.add('col-resizing');
  el.classList.add('is-resizing');

  // Add event listeners
  if (e.type.includes('touch')) {
    document.addEventListener('touchmove', handleResizeMove, {
      passive: false,
    });
    document.addEventListener('touchend', () => handleResizeEnd(options));
  } else {
    document.addEventListener('mousemove', handleResizeMove);
    document.addEventListener('mouseup', () => handleResizeEnd(options));
  }
}

// Handle resize movement
function handleResizeMove(e) {
  if (!resizeState.isResizing) return;

  e.preventDefault();
  const touch = e.type.includes('touch') ? e.touches[0] : e;
  const deltaX = touch.clientX - resizeState.startX;

  const options = resizeState.currentTable._resizableOptions || {};
  const minWidth = options.minWidth || 80;
  const maxWidth = options.maxWidth || 600;

  const newWidth = Math.max(
    minWidth,
    Math.min(maxWidth, resizeState.startWidth + deltaX),
  );

  // Update stored width
  resizeState.columnWidths[resizeState.currentColumn] = newWidth;

  // Apply width immediately
  const table = resizeState.currentTable.querySelector('table');
  const columnIndex = Array.from(table.querySelectorAll('thead th')).findIndex(
    (th) =>
      getColumnKey(
        resizeState.currentTable,
        Array.from(th.parentNode.children).indexOf(th),
      ) === resizeState.currentColumn,
  );

  if (columnIndex !== -1) {
    applyColumnWidth(table, columnIndex, newWidth);
  }
}

// Handle resize end
function handleResizeEnd(options) {
  if (!resizeState.isResizing) return;

  // Save widths
  if (options.tableId) {
    saveColumnWidths(options.tableId, resizeState.columnWidths);
  }

  // Cleanup
  resizeState.isResizing = false;
  resizeState.currentTable.classList.remove('is-resizing');
  resizeState.currentTable = null;
  resizeState.currentColumn = null;

  document.body.classList.remove('col-resizing');
  document.removeEventListener('mousemove', handleResizeMove);
  document.removeEventListener('mouseup', handleResizeEnd);
  document.removeEventListener('touchmove', handleResizeMove);
  document.removeEventListener('touchend', handleResizeEnd);
}

// Setup resize handles
function setupResizeHandles(el, options = {}) {
  const table = el.querySelector('table');
  if (!table) return;

  // Add table layout fixed for consistent column widths
  table.style.tableLayout = 'fixed';

  const headers = table.querySelectorAll('thead th');
  headers.forEach((header, index) => {
    // Skip excluded columns
    const columnKey = getColumnKey(el, index);
    if (options.exclude && options.exclude.includes(columnKey)) return;

    // Skip if already has handle
    if (header.querySelector('.column-resize-handle')) return;

    // Make header relative positioned
    header.style.position = 'relative';

    // Create and add resize handle
    const handle = createResizeHandle(options);

    // Add event listeners
    handle.addEventListener('mousedown', (e) =>
      handleResizeStart(e, el, header, index, options),
    );
    handle.addEventListener(
      'touchstart',
      (e) => handleResizeStart(e, el, header, index, options),
      { passive: false },
    );

    header.appendChild(handle);
  });
}

// Apply saved column widths
function applySavedWidths(el, options = {}) {
  if (!options.tableId) return;

  const table = el.querySelector('table');
  if (!table) return;

  const savedWidths = loadColumnWidths(options.tableId);
  resizeState.columnWidths = { ...savedWidths };

  Object.keys(savedWidths).forEach((columnKey) => {
    const headers = table.querySelectorAll('thead th');
    const columnIndex = Array.from(headers).findIndex(
      (_, index) => getColumnKey(el, index) === columnKey,
    );

    if (columnIndex !== -1) {
      applyColumnWidth(table, columnIndex, savedWidths[columnKey]);
    }
  });
}

// Directive definition
export default {
  bind(el, binding) {
    // Store options on element
    el._resizableOptions = binding.value || {};

    // Wait for table to be rendered
    const observer = new MutationObserver((mutations, obs) => {
      const table = el.querySelector('table');
      if (table && table.querySelector('thead')) {
        obs.disconnect();

        // Setup resize functionality
        setupResizeHandles(el, el._resizableOptions);
        applySavedWidths(el, el._resizableOptions);

        // Watch for table updates
        const tableObserver = new MutationObserver(() => {
          setupResizeHandles(el, el._resizableOptions);
          applySavedWidths(el, el._resizableOptions);
        });

        tableObserver.observe(table, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['class', 'style'],
        });

        el._tableObserver = tableObserver;
      }
    });

    observer.observe(el, {
      childList: true,
      subtree: true,
    });

    el._resizableObserver = observer;
  },

  update(el, binding) {
    // Update options
    el._resizableOptions = binding.value || {};

    // Re-apply setup
    setupResizeHandles(el, el._resizableOptions);
    applySavedWidths(el, el._resizableOptions);
  },

  unbind(el) {
    // Cleanup observers
    if (el._resizableObserver) {
      el._resizableObserver.disconnect();
    }
    if (el._tableObserver) {
      el._tableObserver.disconnect();
    }

    // Remove event listeners
    document.removeEventListener('mousemove', handleResizeMove);
    document.removeEventListener('mouseup', handleResizeEnd);
    document.removeEventListener('touchmove', handleResizeMove);
    document.removeEventListener('touchend', handleResizeEnd);
  },
};
